import { useMemo } from 'react';
import type { ApolloError } from '@apollo/client';
import { useQuery } from '@apollo/client';

import { WebsiteThemeDocument, WebsiteThemeQuery } from 'src/gql/graphql';
import type { WebsiteTheme } from 'src/theme/website-theme-variables';
import { useEnterpriseStorefrontContext } from 'src/dispensary/enterprise-storefront-context';

export type UseWebsiteResult = {
  enterpriseId: string | undefined;
  websiteId: string | undefined;
  /**
   * `undefined` whenever there's nothing to apply -- no enterprise-domain scope at all, no
   * saved theme for this website, or every saved field is null -- callers (`WebsiteThemeVariables`
   * for colors/fonts; the CMS editor's `extensions` consumers go through the separate
   * `ThemeDraftWebsiteThemeBridge` draft path instead, not through this hook today -- see
   * `website-theme-variables.tsx`'s own docblock) should treat that identically to "no theme",
   * not as an error.
   */
  theme: WebsiteTheme | undefined;
  loading: boolean;
  error: ApolloError | undefined;
};

/**
 * Converts the query's response shape into the sparse `WebsiteTheme` shape
 * `WebsiteThemeVariables` consumes -- drops `null` leaves (an explicitly-unset field must stay
 * ABSENT so it falls through the cascade, not become a different kind of falsy `var()` value)
 * and Apollo's own `__typename`. Deliberately a small, local duplicate of the exact same
 * null-pruning shape `themeResponseToDraft`/`pruneNullLeaves` already implement one layer up in
 * `src/cms/components/cms-editor-header/theme-draft.ts` -- not imported from there, to avoid a
 * reverse dependency from `src/dispensary` into `src/cms` (see `website-theme-variables.tsx`'s
 * own docblock for the same layering rule applied to its field-key types).
 */
export function websiteThemeResponseToTheme(
  response: WebsiteThemeQuery['cmsWebsiteTheme'] | undefined
): WebsiteTheme | undefined {
  if (!response) return undefined;

  const theme: WebsiteTheme = {};

  if (response.colors) {
    const colors = Object.fromEntries(
      Object.entries(response.colors).filter(([key, value]) => key !== '__typename' && value != null)
    ) as WebsiteTheme['colors'];
    if (colors && Object.keys(colors).length > 0) theme.colors = colors;
  }

  if (response.fonts) {
    const fonts = Object.fromEntries(
      Object.entries(response.fonts).filter(([key, value]) => key !== '__typename' && value != null)
    ) as WebsiteTheme['fonts'];
    if (fonts && Object.keys(fonts).length > 0) theme.fonts = fonts;
  }

  if (response.extensions) {
    const extensions = Object.fromEntries(
      Object.entries(response.extensions).filter(([key, value]) => key !== '__typename' && value != null)
    ) as WebsiteTheme['extensions'];
    if (extensions && Object.keys(extensions).length > 0) theme.extensions = extensions;
  }

  return Object.keys(theme).length > 0 ? theme : undefined;
}

/**
 * Resolves the website currently in scope for the live storefront (as opposed to
 * `useCmsWebsite`, which resolves "which website" for the CMS editor).
 *
 * Scope comes entirely from `useEnterpriseStorefrontContext()`, which is already wired around
 * both the enterprise-domain CMS page route and the enterprise-domain home page route, and
 * already covers the `{enterprise-cname}.{CMS_PREVIEW_DOMAIN_SUFFIX}` fallback-preview case via
 * the same server-side domain resolution both routes share -- there is no separate preview
 * branch to handle here.
 *
 * `websiteId` is legitimately absent both outside any enterprise-domain context (a normal,
 * non-enterprise store) and on the enterprise-wide fallback (no scoped website resolved yet);
 * callers should treat that as a normal "not applicable" state, not an error.
 *
 * Fetches `theme` (colors/fonts, plus the website's own remote SDK theme module `extensions` --
 * see `WebsiteThemeVariables`'s own docblock for why that's the current scope) via the
 * `cmsWebsiteTheme` query once an enterpriseId AND websiteId have both
 * resolved; skipped (not an error) otherwise, including the enterprise-wide-fallback case where
 * `websiteId` legitimately never resolves. Settings loading remains a further follow-up pending a
 * public settings query existing at all -- this hook is meant to keep gaining fields additively
 * over time without existing callers needing to change.
 */
export function useWebsite(): UseWebsiteResult {
  const enterpriseStorefront = useEnterpriseStorefrontContext();
  const enterpriseId = enterpriseStorefront?.enterpriseId;
  const websiteId = enterpriseStorefront?.websiteId;

  const { data, loading, error } = useQuery(WebsiteThemeDocument, {
    variables: { enterpriseId: enterpriseId ?? '', websiteId: websiteId ?? '' },
    skip: !enterpriseId || !websiteId,
  });

  // Memoized so callers (the `UI.setWebsiteTheme` bridge effects in `HomeCmsContent`/
  // `EnterpriseCmsPage`) get a STABLE object identity across re-renders with the same
  // underlying data -- `websiteThemeResponseToTheme` otherwise allocates a fresh object every
  // call, which an effect keyed on `[UI, theme]` can't tell apart from a real change. A stable
  // identity here is what keeps `UI.websiteTheme`'s own MobX identity stable, which in turn is
  // what `ThemeProvider`'s `stores` useMemo relies on (via its own `websiteTheme` dependency) to
  // avoid rebuilding the styled/MUI theme objects -- and re-rendering every consumer -- on every
  // unrelated re-render of this hook's caller.
  const theme = useMemo(() => websiteThemeResponseToTheme(data?.cmsWebsiteTheme), [data?.cmsWebsiteTheme]);

  return {
    enterpriseId,
    websiteId,
    theme,
    loading,
    error,
  };
}

export default useWebsite;
